import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import type { EventListItem } from "../../types";
import { StatusChip } from "../ui/StatusChip";
import { resolveHoldLevel } from "../../utils/eventHoldLevel";
import { formatINR, formatSellThroughPct } from "../../utils/format";
import "./CalendarEventPopover.css";

type EventModeProps = {
  mode?: "event";
  event: EventListItem;
  sellThrough?: number | null;
  revenueBooked?: number | null;
  ticketsSold?: number | null;
  overviewPath: string;
  livePath: string;
  editPath: string;
  onAddEvent?: never;
  dateKey?: never;
  events?: never;
};

type DayModeProps = {
  mode: "day";
  dateKey: string;
  dateLabel: string;
  events: EventListItem[];
  onAddEvent?: () => void;
  addDisabled?: boolean;
  onOpenEvent: (event: EventListItem) => void;
  onEditEvent?: (event: EventListItem) => void;
  event?: never;
  overviewPath?: never;
  livePath?: never;
  editPath?: never;
};

type Props = {
  anchorRect: DOMRect;
  pinned?: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
} & (EventModeProps | DayModeProps);

function formatEventTime(event: EventListItem): string | null {
  if (!event.start_date) return null;
  const start = new Date(event.start_date);
  if (Number.isNaN(start.getTime())) return null;
  return start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function statusToneClass(status: string): string {
  const s = status.toLowerCase().replace(/\s+/g, "_");
  if (s.includes("hold") || s === "planned" || s === "draft") return "is-hold";
  if (s === "confirmed" || s === "active") return "is-confirmed";
  if (s.includes("on_sale")) return "is-sale";
  if (s.includes("live")) return "is-live";
  if (s === "completed" || s === "settled") return "is-done";
  if (s.includes("cancel")) return "is-cancel";
  return "is-neutral";
}

function placePopover(anchorRect: DOMRect, panelWidth: number, panelHeight: number) {
  const gap = 10;
  const margin = 12;
  const spaceBelow = window.innerHeight - anchorRect.bottom - margin;
  const spaceAbove = anchorRect.top - margin;
  const placeAbove = spaceBelow < Math.min(panelHeight, 220) && spaceAbove > spaceBelow;

  const top = placeAbove
    ? Math.max(margin, anchorRect.top - panelHeight - gap)
    : Math.min(anchorRect.bottom + gap, window.innerHeight - Math.min(panelHeight, window.innerHeight - margin * 2) - margin);

  const left = Math.min(
    Math.max(margin, anchorRect.left + anchorRect.width / 2 - panelWidth / 2),
    window.innerWidth - panelWidth - margin,
  );

  return { top, left, placeAbove };
}

export const CalendarEventPopover: React.FC<Props> = (props) => {
  const { anchorRect, pinned = false, onClose, onMouseEnter, onMouseLeave } = props;
  const panelWidth = props.mode === "day" && !pinned ? 300 : 320;
  const estimatedHeight = props.mode === "day" ? Math.min(120 + props.events.length * 72, 340) : 220;
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState(() => placePopover(anchorRect, panelWidth, estimatedHeight));

  // The estimate above is only a guess — measure the panel once it's actually
  // in the DOM and re-clamp against its real size. Without this, content
  // taller/narrower than estimated (long event lists, small viewports) can
  // still poke past the edge of the screen. Runs before paint so there's no
  // visible jump.
  useLayoutEffect(() => {
    const node = panelRef.current;
    const rect = node?.getBoundingClientRect();
    setPlacement(placePopover(anchorRect, rect?.width || panelWidth, rect?.height || estimatedHeight));
  }, [anchorRect, panelWidth, estimatedHeight]);

  const { top, left, placeAbove } = placement;

  const panel = (
    <div
      ref={panelRef}
      className={`calendar-popover${pinned ? " calendar-popover--pinned" : " calendar-popover--hover"}${
        props.mode === "day" ? " calendar-popover--day" : ""
      }${placeAbove ? " calendar-popover--above" : ""}`}
      style={{ top, left, width: Math.min(panelWidth, window.innerWidth - 24) }}
      role="dialog"
      aria-label={props.mode === "day" ? `Events on ${props.dateLabel}` : `${props.event.name} details`}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {props.mode === "day" ? (
        <>
          <div className="calendar-popover__head calendar-popover__head--day">
            <div className="calendar-popover__date-block">
              <span className="calendar-popover__eyebrow">Schedule</span>
              <strong className="calendar-popover__title">{props.dateLabel}</strong>
            </div>
            <div className="calendar-popover__head-end">
              <span className="calendar-popover__count">
                {props.events.length}
                <span className="calendar-popover__count-label">
                  {props.events.length === 1 ? "show" : "shows"}
                </span>
              </span>
              {pinned ? (
                <button
                  type="button"
                  className="calendar-popover__close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>

          {props.events.length === 0 ? (
            <p className="calendar-popover__empty">No events on this date.</p>
          ) : (
            <ul className="calendar-popover__day-list">
              {props.events.map((event) => {
                const time = formatEventTime(event);
                const hold = resolveHoldLevel(event);
                return (
                  <li key={event.id} className={`calendar-popover__day-item ${statusToneClass(event.status)}`}>
                    <button
                      type="button"
                      className="calendar-popover__day-open"
                      onClick={() => props.onOpenEvent(event)}
                    >
                      <span className="calendar-popover__day-accent" aria-hidden />
                      <span className="calendar-popover__day-body">
                        <span className="calendar-popover__day-row">
                          <span className="calendar-popover__day-name">{event.name}</span>
                          <StatusChip status={event.status} holdLevel={hold} holdLabel={event.hold_label} compact />
                        </span>
                        <span className="calendar-popover__day-meta">
                          {time ? <span className="calendar-popover__day-time">{time}</span> : null}
                          {event.artist ? <span>{event.artist}</span> : null}
                          {event.city ? <span>{event.city}</span> : null}
                          {event.venue_capacity ? (
                            <span>{event.venue_capacity.toLocaleString("en-IN")} cap</span>
                          ) : null}
                        </span>
                      </span>
                    </button>
                    {props.onEditEvent ? (
                      <button
                        type="button"
                        className="eventos-nav-btn calendar-popover__day-edit"
                        onClick={() => props.onEditEvent?.(event)}
                      >
                        Edit
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          {pinned || props.onAddEvent ? (
            <div className="calendar-popover__actions">
              {props.onAddEvent ? (
                <button
                  type="button"
                  className="eventos-primary-btn calendar-popover__link"
                  onClick={props.onAddEvent}
                  disabled={props.addDisabled}
                  title={props.addDisabled ? "Cannot schedule events on past dates" : undefined}
                >
                  + Add Event
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="calendar-popover__head">
            <strong className="calendar-popover__title">{props.event.name}</strong>
            <StatusChip
              status={props.event.status}
              holdLevel={resolveHoldLevel(props.event)}
              holdLabel={props.event.hold_label}
              compact
            />
          </div>
          {props.event.artist ? <div className="calendar-popover__meta">{props.event.artist}</div> : null}
          <div className="calendar-popover__meta">
            {props.event.city} · {props.event.event_date}
            {props.event.venue_capacity ? ` · ${props.event.venue_capacity.toLocaleString("en-IN")} cap` : ""}
          </div>
          {props.sellThrough != null || props.revenueBooked != null ? (
            <div className="calendar-popover__stats">
              {props.sellThrough != null ? (
                <span>Sell-through {formatSellThroughPct(props.sellThrough, props.ticketsSold ?? 0)}</span>
              ) : null}
              {props.revenueBooked != null ? <span>Revenue {formatINR(props.revenueBooked)}</span> : null}
            </div>
          ) : null}
          <div className="calendar-popover__actions">
            <Link to={props.overviewPath} className="eventos-primary-btn calendar-popover__link" onClick={onClose}>
              Open event
            </Link>
            <Link to={props.editPath} className="eventos-nav-btn calendar-popover__link" onClick={onClose}>
              Edit
            </Link>
            <Link to={props.livePath} className="eventos-nav-btn calendar-popover__link" onClick={onClose}>
              Live Ops
            </Link>
          </div>
        </>
      )}
    </div>
  );

  const tree = !pinned ? (
    panel
  ) : (
    <div className="calendar-popover__backdrop" role="presentation" onClick={onClose}>
      {panel}
    </div>
  );

  if (typeof document === "undefined") return tree;
  return createPortal(tree, document.body);
};
